import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Anchor, Eye, EyeOff, Loader2, Target, MessageSquare, Link2, Users, Globe, Smartphone, Mail } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LoginPage = () => {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    setLoading(true);
    try {
      await login(email, password);
      toast.success('Connexion réussie !');
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error('Veuillez entrer votre email');
      return;
    }

    setForgotLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/forgot-password`, { email: forgotEmail });
      toast.success('Si un compte existe avec cet email, vous recevrez un email de réinitialisation.');
      setShowForgotPassword(false);
      setForgotEmail('');
    } catch (error) {
      toast.error('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setForgotLoading(false);
    }
  };

  const features = [
    { icon: Users, text: "Échangez avec des investisseurs passionnés" },
    { icon: Globe, text: "Accessible partout, tout le temps" },
    { icon: Smartphone, text: "Application mobile installable" },
  ];

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Hero */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        {/* Background with ocean/sea */}
        <div className="absolute inset-0">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url('https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1200&q=80')`,
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/80 to-slate-900/60" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent" />
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <img 
              src="/logo-192.png" 
              alt="L'Équipage Odys" 
              className="w-12 h-12 rounded-xl"
            />
            <span className="text-xl font-heading font-bold text-white">
              L'Équipage <span style={{ color: '#B5894A' }}>Odys</span>
            </span>
          </div>

          {/* Main text */}
          <div className="max-w-lg">
            <h1 className="text-4xl font-heading font-bold text-white leading-tight mb-2">
              Une communauté
            </h1>
            <h1 className="text-4xl font-heading font-bold leading-tight mb-2" style={{ color: '#B5894A' }}>
              d'investisseurs immobiliers
            </h1>
            <h1 className="text-4xl font-heading font-bold text-white leading-tight mb-2">
              engagés
            </h1>
            <p className="text-2xl font-medium mt-4 mb-8" style={{ color: '#E9DCC4' }}>
              accessible partout !
            </p>
            <p className="text-lg text-white/70 leading-relaxed">
              Rejoignez une communauté de passionnés qui s'entraident 
              pour réussir leurs projets d'investissement immobilier.
            </p>
          </div>

          {/* Features */}
          <div className="space-y-4">
            {features.map((feature, index) => (
              <div key={index} className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-amber-500/20 border border-amber-500/30 flex items-center justify-center">
                  <feature.icon className="w-5 h-5 text-amber-400" />
                </div>
                <span className="text-white/90">{feature.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-12 bg-slate-900">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <div className="inline-flex items-center gap-3">
              <img 
                src="/logo-192.png" 
                alt="L'Équipage Odys" 
                className="w-12 h-12 rounded-xl"
              />
              <span className="text-xl font-heading font-bold text-white">
                L'Équipage <span style={{ color: '#B5894A' }}>Odys</span>
              </span>
            </div>
          </div>

          {/* Login Card */}
          <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-heading font-bold text-white mb-2">
                Bon retour !
              </h2>
              <p className="text-slate-400">
                Connectez-vous à votre espace communautaire
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-300 text-sm">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="vous@exemple.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-slate-900/50 border-slate-600/50 text-white placeholder:text-slate-500 h-12 rounded-xl"
                  style={{ '--tw-ring-color': '#B5894A' }}
                  data-testid="login-email-input"
                  disabled={loading}
                />
              </div>
              
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label htmlFor="password" className="text-slate-300 text-sm">Mot de passe</Label>
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-xs transition-colors hover:underline"
                    style={{ color: '#B5894A' }}
                  >
                    Mot de passe oublié ?
                  </button>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="bg-slate-900/50 border-slate-600/50 text-white placeholder:text-slate-500 h-12 rounded-xl pr-10"
                    data-testid="login-password-input"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-12 rounded-xl font-semibold text-base transition-all"
                style={{
                  background: 'linear-gradient(90deg, #B5894A 0%, #8C6635 100%)',
                  color: '#16233B',
                  boxShadow: '0 6px 18px rgba(181, 137, 74, 0.35)',
                }}
                disabled={loading}
                data-testid="login-submit-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Connexion...
                  </>
                ) : (
                  'Se connecter'
                )}
              </Button>

              {/* Google Sign-In (Emergent-managed OAuth) — connexion uniquement, aucune création */}
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-slate-700/60"></span>
                </div>
                <div className="relative flex justify-center text-xs uppercase tracking-wider">
                  <span className="bg-transparent px-2 text-slate-400">ou</span>
                </div>
              </div>

              <button
                type="button"
                onClick={() => {
                  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                  const redirectUrl = window.location.origin + '/dashboard';
                  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                }}
                className="w-full h-12 rounded-xl font-semibold text-base flex items-center justify-center gap-2.5 transition-all hover:-translate-y-0.5"
                style={{ background: '#ffffff', color: '#0F2540', border: '1px solid rgba(15,37,64,0.15)' }}
                data-testid="google-signin-btn"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                Se connecter avec Google
              </button>

              <p className="text-center text-xs text-slate-400 pt-2 leading-relaxed">
                En te connectant, tu acceptes nos{' '}
                <a
                  href="https://odysinvest.com/conditions-generales-dutilisation/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline underline-offset-2 hover:opacity-80 transition-opacity"
                  style={{ color: '#C9A86A' }}
                  data-testid="login-cgu-link"
                >
                  Conditions Générales d'Utilisation
                </a>{' '}
                et notre{' '}
                <a
                  href="https://odysinvest.com/politique-de-confidentialite/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline underline-offset-2 hover:opacity-80 transition-opacity"
                  style={{ color: '#C9A86A' }}
                  data-testid="login-privacy-link"
                >
                  Politique de confidentialité
                </a>
                .
              </p>
            </form>
          </div>
        </div>
      </div>

      {/* Forgot Password Modal */}
      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Mail className="w-5 h-5 text-cyan-400" />
              Mot de passe oublié
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleForgotPassword} className="space-y-4 mt-4">
            <p className="text-slate-400 text-sm">
              Entrez votre adresse email et nous vous enverrons un nouveau mot de passe temporaire.
            </p>
            <div className="space-y-2">
              <Label htmlFor="forgot-email" className="text-slate-300">Email</Label>
              <Input
                id="forgot-email"
                type="email"
                placeholder="vous@exemple.com"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                className="bg-slate-900/50 border-slate-600/50 text-white placeholder:text-slate-500 focus:border-cyan-500"
                disabled={forgotLoading}
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowForgotPassword(false)}
                className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
                disabled={forgotLoading}
              >
                Annuler
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-cyan-500 hover:bg-cyan-600"
                disabled={forgotLoading}
              >
                {forgotLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Envoi...
                  </>
                ) : (
                  'Envoyer'
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LoginPage;
